import React, { useMemo, useState } from "react";
import { Send, Ban, AlertTriangle, Search } from "lucide-react";
import { usePreferences } from "../../context/PreferencesContext";
import { useTranslate } from "../../i18n/useTranslate";
import { useAuth } from "../../auth/AuthContext";
import { LiveSection, Stat, Assumptions } from "./primitives";
import { RESPONSE_ASSUMPTIONS, CASE_TYPES } from "../../geo/services/emergencyResponse";
import { ask, INTENTS, COPILOT_ASSUMPTIONS } from "../../intelligence/copilot";
import { buildPilgrimView, PILGRIM_ASSUMPTIONS } from "../../governance/pilgrimView";

const useL = () => {
  const { lang } = usePreferences();
  return (o) => (typeof o === "string" ? o : o?.[lang] ?? o?.en ?? "");
};

/* ── §12 Emergency response ────────────────────────────────────────────── */
export function LiveResponseSection({ respondTo }) {
  const t = useTranslate();
  const L = useL();
  const [caseType, setCaseType] = useState("cardiac");

  const plan = useMemo(
    () => respondTo({
      id: "LIVE-01", caseType, sector: "SEC-01", coord: [73.7900, 19.9985],
    }),
    [respondTo, caseType],
  );

  return (
    <LiveSection
      title="Emergency Response Planner"
      section="§12"
      note="Nearest is not the same as appropriate, and distance is not time. Crowd density along the approach is applied as a speed penalty."
    >
      <div className="mb-3 flex flex-wrap gap-1.5">
        {Object.entries(CASE_TYPES).map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => setCaseType(k)}
            className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${
              caseType === k
                ? "border-indigo-400 bg-indigo-100 text-indigo-900"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {L(v.label)}
          </button>
        ))}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Projected response"
          value={plan.estimatedResponseMinutes ?? "—"}
          unit="min"
          tone={plan.withinGoldenWindow === false ? "red" : "green"}
        />
        <Stat label="Clinical window" value={plan.goldenWindowMinutes} unit="min" />
        <Stat
          label="Crowd speed factor"
          value={plan.primaryResponder?.crowdSpeedFactor != null
            ? `${Math.round(plan.primaryResponder.crowdSpeedFactor * 100)}%`
            : "—"}
          tone="amber"
        />
        <Stat label="Supporting agencies" value={plan.supportingAgencies.length} tone="indigo" />
      </div>

      <div className="mb-3 grid gap-2 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
            {t("Recommended responder")}
          </div>
          {plan.primaryResponder ? (
            <>
              <p className="text-sm font-black text-slate-900">{L(plan.primaryResponder.name)}</p>
              <p className="text-[11px] text-slate-600">
                {plan.primaryResponder.distanceKm} km ·{" "}
                {plan.primaryResponder.effectiveSpeedKmh} km/h {t("effective")}
                {plan.primaryResponder.advancedLifeSupport && ` · ${t("ALS")}`}
              </p>
            </>
          ) : (
            <p className="text-xs text-red-700">{t("No appropriate responder available.")}</p>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
            {t("Receiving facility")}
          </div>
          {plan.receivingFacility ? (
            <>
              <p className="text-sm font-black text-slate-900">{L(plan.receivingFacility.name)}</p>
              <p className="text-[11px] text-slate-600">
                {plan.receivingFacility.emergencyFree} {t("emergency bays free")} ·{" "}
                {plan.receivingFacility.transferMinutes} {t("min transfer")}
              </p>
            </>
          ) : (
            <p className="text-xs text-red-700">{t("No facility with free capacity and the required capability.")}</p>
          )}
        </div>
      </div>

      {plan.warnings.length > 0 && (
        <div className="mb-3 space-y-1">
          {plan.warnings.map((w) => (
            <div key={w} className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5">
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0 text-amber-700" />
              <p className="text-[11px] leading-snug text-amber-900">{w}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-3 py-2">
        <Ban size={12} className="mt-0.5 flex-shrink-0 text-slate-600" />
        <p className="text-[11px] leading-snug text-slate-700">{plan.authorisationNote}</p>
      </div>

      <Assumptions items={RESPONSE_ASSUMPTIONS} />
    </LiveSection>
  );
}

/* ── §13 Field officer copilot ─────────────────────────────────────────── */
export function LiveCopilotSection({ live, twin, incidents = [] }) {
  const t = useTranslate();
  const L = useL();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);

  /* The officer's clearance comes from their profile, so the copilot
   * behaves differently for a Divisional Commissioner and a Kumbhathon
   * integration partner — which is the point of §17 being enforced rather
   * than displayed. */
  const role = user?.clearance ?? "FIELD";

  const run = (question) => {
    setQ(question);
    setAnswer(ask(question, {
      liveState: live.state, twin, role, incidents,
      officerCoord: [73.7900, 19.9985],
    }));
  };

  return (
    <LiveSection
      title="Field Officer Copilot"
      section="§13"
      note="A read-only intelligence interface. It has no write path to the incident machine, a gate or a PA system, so it cannot command action even if asked directly."
    >
      <div className="mb-2 flex items-center gap-2">
        <div className="flex flex-1 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2">
          <Search size={13} className="text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(q)}
            placeholder={t("Ask about risks, procedure, capacity or nearby incidents")}
            className="flex-1 bg-transparent py-1.5 text-xs text-slate-800 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => run(q)}
          className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"
        >
          <Send size={12} /> {t("Ask")}
        </button>
      </div>

      <div className="mb-3 flex flex-wrap gap-1">
        <span className="text-[10px] font-bold text-slate-400">{t("Signed in as")}:</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-black text-slate-700">
          {role}
        </span>
        {Object.values(INTENTS).slice(0, 4).map((i) => (
          <button
            key={i.id}
            type="button"
            onClick={() => run(i.example)}
            className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-50"
          >
            {i.example}
          </button>
        ))}
      </div>

      {answer && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          {answer.interpretedAs && (
            <div className="mb-1.5 text-[10px] text-slate-500">
              <strong>{t("Understood as")}:</strong> {answer.interpretedAs}
            </div>
          )}

          {answer.refused ? (
            <div className={`rounded-lg border px-3 py-2 ${
              answer.refusalKind === "command" ? "border-red-300 bg-red-50"
              : answer.refusalKind === "access" ? "border-orange-300 bg-orange-50"
              : "border-slate-300 bg-white"
            }`}>
              <div className="mb-0.5 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-600">
                <Ban size={11} /> {t("Refused")} · {answer.refusalKind}
              </div>
              <p className="text-xs leading-snug text-slate-800">{answer.message}</p>
              {answer.whatToDoInstead && (
                <p className="mt-1.5 border-t border-black/5 pt-1.5 text-[11px] leading-snug text-slate-600">
                  <strong>{t("Instead")}:</strong> {answer.whatToDoInstead}
                </p>
              )}
              {answer.canAnswer && (
                <ul className="mt-1.5 space-y-0.5">
                  {answer.canAnswer.map((c) => (
                    <li key={c.id} className="text-[11px] text-slate-600">· {c.example}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <CopilotAnswer answer={answer.answer} L={L} t={t} />
          )}

          <p className="mt-2 text-[10px] italic text-slate-500">{answer.advisoryOnly}</p>
        </div>
      )}

      <Assumptions items={COPILOT_ASSUMPTIONS} />
    </LiveSection>
  );
}

function CopilotAnswer({ answer, L, t }) {
  if (!answer) return null;

  if (answer.kind === "needs_input") {
    return <p className="text-xs leading-snug text-slate-700">{answer.message}</p>;
  }

  if (answer.kind === "documents") {
    return (
      <div>
        <div className="mb-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] font-bold leading-snug text-amber-900">
          {answer.warning}
        </div>
        {answer.documents?.map((d) => (
          <div key={d.id} className="mb-1.5 rounded-lg border border-slate-200 bg-white p-2">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-black text-slate-900">{L(d.title)}</span>
              <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-bold text-slate-500">{d.id}</span>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-[11px] leading-snug text-slate-700">{d.body}</pre>
            <p className="mt-1 text-[10px] italic text-slate-400">{d.source}</p>
          </div>
        ))}
      </div>
    );
  }

  if (answer.kind === "risks") {
    return (
      <div>
        <p className="mb-1.5 text-xs leading-snug text-slate-800">{answer.summary}</p>
        <div className="flex flex-wrap gap-1">
          {answer.top.map((h) => (
            <span key={h.key} className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px]">
              {L(h.label)} <strong>{Math.round(h.severity * 100)}</strong>
            </span>
          ))}
        </div>
        {answer.notMonitored?.length > 0 && (
          <p className="mt-1.5 text-[10px] text-slate-500">
            <strong>{t("Not monitored")}:</strong> {answer.notMonitored.join(", ")}
          </p>
        )}
      </div>
    );
  }

  if (answer.kind === "capacity") {
    return (
      <div>
        <table className="w-full text-[11px]">
          <tbody>
            {answer.facilities.slice(0, 5).map((f) => (
              <tr key={f.id} className="border-b border-slate-200">
                <td className="py-1 text-slate-700">{L(f.name)}</td>
                <td className="py-1 text-right tabular-nums text-slate-900">
                  {f.emergencyFree == null ? t("unknown") : `${f.emergencyFree} free`}
                </td>
                <td className="py-1 text-right tabular-nums text-slate-500">
                  {f.distanceKm != null ? `${f.distanceKm} km` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-1 text-[10px] italic text-slate-500">{answer.note}</p>
      </div>
    );
  }

  if (answer.kind === "incidents") {
    return answer.count === 0 ? (
      <p className="text-xs text-slate-600">
        {t("No unresolved incidents within")} {answer.radiusKm} km.
      </p>
    ) : (
      <ul className="space-y-0.5">
        {answer.incidents.map((i) => (
          <li key={i.id} className="text-[11px] text-slate-700">
            {i.id} · {i.sector} · {i.status} · {i.distanceKm} km
          </li>
        ))}
      </ul>
    );
  }

  if (answer.kind === "services") {
    return answer.allClear ? (
      <p className="text-xs text-emerald-800">{t("All services within capacity here.")}</p>
    ) : (
      <ul className="space-y-1">
        {answer.atRisk.map((s) => (
          <li key={s.service} className="text-[11px] leading-snug text-slate-700">· {s.narrative}</li>
        ))}
      </ul>
    );
  }

  if (answer.kind === "forecast") {
    return (
      <div>
        {[15, 30, 60].map((h) => (
          <div key={h} className="mb-0.5 text-[11px] text-slate-700">
            <strong>+{h}m:</strong>{" "}
            {answer.horizons[h].map((s) => `${L(s.name)} ${s.band} (${s.occupancyPct}%)`).join(" · ")}
          </div>
        ))}
        <p className="mt-1 text-[10px] italic text-slate-500">{answer.note}</p>
      </div>
    );
  }

  return <p className="text-xs text-slate-600">{answer.message ?? ""}</p>;
}

/* ── §14 Pilgrim intelligence ──────────────────────────────────────────── */
export function LivePilgrimSection({ live, twin }) {
  const t = useTranslate();
  const L = useL();
  const { lang } = usePreferences();
  const view = useMemo(
    () => buildPilgrimView(live.state, { twin, lang }),
    [live.state, twin, lang],
  );

  if (!view.available) {
    return (
      <LiveSection title="Pilgrim Intelligence" section="§14">
        <p className="text-xs text-slate-600">{view.reason}</p>
      </LiveSection>
    );
  }

  const LEVEL = {
    comfortable: "border-emerald-300 bg-emerald-50 text-emerald-900",
    busy: "border-amber-300 bg-amber-50 text-amber-900",
    very_busy: "border-orange-300 bg-orange-50 text-orange-900",
    restricted: "border-red-300 bg-red-50 text-red-900",
    unknown: "border-slate-300 bg-slate-100 text-slate-600",
  };

  return (
    <LiveSection
      title="Pilgrim View (public projection)"
      section="§14"
      note="Derived from the operational data through the enforced §17 redaction filter, not assembled separately. A field can only reach a pilgrim if it survives a filter that deletes rather than hides."
    >
      {/* Everything inside this region is what a pilgrim actually sees.
          The method notes below it are written for a reviewer and discuss
          the data the pilgrim is NOT given — so a leak scan has to be able
          to tell the two apart. The boundary is marked rather than implied. */}
      <div data-testid="pilgrim-payload">
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Ghats published" value={view.ghats.length} />
        <Stat label="Open now" value={view.openGhatCount} tone="green" />
        <Stat label="Facilities shown" value={view.facilities.length} />
        <Stat label="Fields redacted" value={view.redactedFields.length} tone="indigo" />
      </div>

      <div className="mb-3 grid gap-1.5 md:grid-cols-2">
        {view.ghats.map((g) => (
          <div key={g.id} className={`rounded-xl border p-2.5 ${LEVEL[g.crowdLevel]}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-black">{L(g.name)}</span>
              <span className="ml-auto rounded bg-white/70 px-1.5 py-0.5 text-[9px] font-black uppercase">
                {L(g.crowdLabel)}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] leading-snug">{L(g.advice)}</p>
          </div>
        ))}
      </div>

      {view.recommendedGhat && (
        <div className="mb-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2">
          <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">
            {t("Recommended")}
          </span>
          <p className="text-sm font-black text-emerald-900">{L(view.recommendedGhat.name)}</p>
        </div>
      )}

      <div className="rounded-xl border border-slate-300 bg-slate-100 px-3 py-2">
        <p className="text-[11px] leading-snug text-slate-700">{L(view.notice)}</p>
      </div>
      </div>

      <Assumptions items={PILGRIM_ASSUMPTIONS} />
    </LiveSection>
  );
}
